import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ipdAPI, ipdBillingAPI, pharmacyWardAPI } from '../../utils/api';
import { printDischargeSlip, printIPDDischargeSummary } from '../../utils/printSlip';

const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };

const printAdmissionSlip = (a) => {
    const now = new Date().toLocaleString('en-PK');
    const admDate = a.admission_time ? new Date(a.admission_time).toLocaleString('en-PK') : now;
    const age = [a.age_years ? `${a.age_years}Y` : '', a.age_months ? `${a.age_months}M` : ''].filter(Boolean).join(' ') || '—';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Admission Slip</title><style>
    *{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,sans-serif;background:#e8ecf0;padding:24px 16px;color:#111;}
    .page{background:#fff;max-width:780px;margin:0 auto;padding:28px 32px;box-shadow:0 4px 24px rgba(0,0,0,.18);}
    .header{text-align:center;border-bottom:2px solid #1e3a5f;padding-bottom:10px;margin-bottom:14px;}
    .hosp-name{font-size:18px;font-weight:800;color:#1e3a5f;letter-spacing:1px;}
    .hosp-sub{font-size:11px;color:#666;margin-top:2px;}
    .slip-title{font-size:13px;font-weight:700;color:#1e3a5f;border:1.5px solid #1e3a5f;display:inline-block;padding:3px 18px;margin-top:8px;letter-spacing:.5px;}
    .grid2{display:grid;grid-template-columns:1fr 1fr;gap:0;}
    .grid3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:0;}
    .field{padding:6px 8px;border-bottom:1px solid #bbb;font-size:12px;min-height:30px;}
    .field:nth-child(odd){border-right:1px solid #bbb;}
    .flabel{font-size:10px;color:#444;font-weight:600;text-transform:uppercase;letter-spacing:.3px;display:block;margin-bottom:2px;}
    .fval{font-size:13px;font-weight:700;color:#111;}
    .fval.big{font-size:14px;color:#1e3a5f;}
    .sec-bar{background:#1e3a5f;color:white;font-size:11px;font-weight:700;padding:4px 10px;letter-spacing:.5px;text-transform:uppercase;margin-top:10px;}
    .sec-border{border:1px solid #bbb;border-top:none;}
    .mo-bar{border:1.5px solid #1e3a5f;text-align:center;font-size:11px;font-weight:700;color:#1e3a5f;padding:4px;margin-top:12px;letter-spacing:1px;}
    .blank-grid{display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #bbb;border-top:none;}
    .blank-field{padding:8px 10px 6px;border-bottom:1px solid #bbb;font-size:11px;font-weight:700;color:#444;min-height:38px;}
    .blank-field:nth-child(odd){border-right:1px solid #bbb;}
    .blank-line{border-bottom:1px solid #111;margin-top:10px;margin-left:4px;}
    .diag-grid{display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #bbb;border-top:none;}
    .diag-col{padding:8px 10px;}.diag-col:first-child{border-right:1px solid #bbb;}
    .diag-title{font-size:11px;font-weight:700;color:#444;text-transform:uppercase;margin-bottom:6px;}
    .diag-line{border-bottom:1px solid #111;margin-bottom:8px;padding-bottom:2px;font-size:11px;color:#999;}
    .remarks-row{border:1px solid #bbb;border-top:none;padding:8px 10px;}
    .remarks-blank{border-bottom:1px solid #111;margin-top:12px;}
    .footer{margin-top:20px;display:grid;grid-template-columns:1fr 1fr;gap:20px;padding-top:8px;border-top:1px solid #bbb;}
    .sig-line-box{border:1px solid #bbb;padding:20px 14px 6px;font-size:10px;color:#444;font-weight:600;text-align:center;display:inline-block;min-width:200px;}
    @media print{body{background:white;padding:0;}.page{box-shadow:none;padding:16px;}}
    </style></head><body><div class="page">
    <div class="header"><div class="hosp-name">CITY HOSPITAL</div><div class="hosp-sub">123 Hospital Road, Karachi &nbsp;|&nbsp; Tel: 021-1234567</div><div class="slip-title">IPD ADMISSION SLIP</div></div>
    <div class="grid3 sec-border" style="border-top:1px solid #bbb">
        <div class="field" style="border-right:1px solid #bbb"><span class="flabel">Admission No.</span><span class="fval big">${a.admission_number||'—'}</span></div>
        <div class="field" style="border-right:1px solid #bbb"><span class="flabel">Registration No (MRN)</span><span class="fval big">${a.mrn||'—'}</span></div>
        <div class="field"><span class="flabel">Date &amp; Time</span><span class="fval">${admDate}</span></div>
    </div>
    <div class="sec-bar">Patient Information</div>
    <div class="grid2 sec-border">
        <div class="field" style="border-right:1px solid #bbb"><span class="flabel">Patient Full Name</span><span class="fval">${a.full_name||'—'}</span></div>
        <div class="field"><span class="flabel">Contact No</span><span class="fval">${a.phone||'—'}</span></div>
        <div class="field" style="border-right:1px solid #bbb"><span class="flabel">Gender</span><span class="fval">${a.gender||'—'}</span></div>
        <div class="field"><span class="flabel">Age</span><span class="fval">${age}</span></div>
        <div class="field" style="border-right:1px solid #bbb"><span class="flabel">CNIC</span><span class="fval">${a.cnic||'—'}</span></div>
        <div class="field"><span class="flabel">Blood Group</span><span class="fval">${a.blood_group||'—'}</span></div>
    </div>
    <div class="sec-bar">Admission Details</div>
    <div class="grid2 sec-border">
        <div class="field" style="border-right:1px solid #bbb"><span class="flabel">Department</span><span class="fval">${a.department||'—'}</span></div>
        <div class="field"><span class="flabel">Consultant Name</span><span class="fval">${a.admitting_doctor||'—'}</span></div>
        <div class="field" style="border-right:1px solid #bbb"><span class="flabel">Ward Name</span><span class="fval">${a.ward_name||'—'}</span></div>
        <div class="field"><span class="flabel">Mode of Admission</span><span class="fval">${a.admission_purpose||'—'}</span></div>
        <div class="field" style="border-right:1px solid #bbb"><span class="flabel">Bed No.</span><span class="fval">${a.bed_number||'—'}</span></div>
        <div class="field"><span class="flabel">Diagnosis</span><span class="fval">${a.diagnosis||'—'}</span></div>
    </div>
    <div class="sec-bar">Relative to be Informed</div>
    <div class="grid2 sec-border">
        <div class="field" style="border-right:1px solid #bbb"><span class="flabel">Name</span><span class="fval">${a.next_of_kin_name||'—'}</span></div>
        <div class="field"><span class="flabel">Relation</span><span class="fval">${a.next_of_kin_relation||'—'}</span></div>
        <div class="field" style="border-right:1px solid #bbb;border-bottom:none"><span class="flabel">Contact No</span><span class="fval">${a.next_of_kin_contact||'—'}</span></div>
        <div class="field" style="border-bottom:none"></div>
    </div>
    <div class="mo-bar">— TO BE FILLED IN BY M.O / C —</div>
    <div class="blank-grid">
        <div class="blank-field">HEIGHT (In Inches)<div class="blank-line"></div></div>
        <div class="blank-field">WEIGHT (In Kgs)<div class="blank-line"></div></div>
        <div class="blank-field">TEMPERATURE (F)<div class="blank-line"></div></div>
        <div class="blank-field">BLOOD PRESSURE<div class="blank-line"></div></div>
    </div>
    <div class="diag-grid">
        <div class="diag-col"><div class="diag-title">Final Diagnosis</div><div class="diag-line">1.&nbsp;</div><div class="diag-line">2.&nbsp;</div><div class="diag-line">3.&nbsp;</div></div>
        <div class="diag-col"><div class="diag-title">Associated Diagnosis</div><div class="diag-line">1.&nbsp;</div><div class="diag-line">2.&nbsp;</div><div class="diag-line">3.&nbsp;</div></div>
    </div>
    <div class="remarks-row"><span style="font-size:11px;font-weight:700;color:#444">REMARKS:</span><div class="remarks-blank"></div><div class="remarks-blank" style="margin-top:10px"></div></div>
    <div class="footer">
        <div style="font-size:10.5px;color:#444"><div><strong>Admission Processed By:</strong> Reception</div><div><strong>Processed On:</strong> ${now}</div></div>
        <div style="text-align:right"><div class="sig-line-box">Signature &amp; Stamp of Admin. Dept</div></div>
    </div>
    </div><script>window.onload=()=>{window.print();}</script></body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    w.document.write(html); w.document.close();
};

const printConsentForm = (a) => {
    const now = new Date().toLocaleString('en-PK');
    const admDate = a.admission_time ? new Date(a.admission_time).toLocaleString('en-PK') : now;
    const ptName  = a.full_name || '—';
    const guardian = a.next_of_kin_name ? `${a.next_of_kin_name}${a.next_of_kin_relation ? ' ('+a.next_of_kin_relation+')' : ''}` : '—';
    const cnic = a.next_of_kin_cnic || a.cnic || '';
    const advancePaid = a.advance_paid ? `Rs. ${parseFloat(a.advance_paid).toLocaleString()}` : 'As per billing';
    const receiptNo = a.advance_receipt_no || '—';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Consent Form</title>
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Source+Sans+3:wght@400;600;700&display=swap">
    <style>
    *{box-sizing:border-box;margin:0;padding:0;}
    body{background:#d9dee5;font-family:'Source Sans 3',Arial,sans-serif;color:#111;padding:28px 16px;}
    .page{background:#fff;max-width:740px;margin:0 auto;padding:30px 36px 26px;box-shadow:0 4px 28px rgba(0,0,0,.22);}
    .hdr{text-align:center;border-bottom:2.5px solid #1e3a5f;padding-bottom:11px;margin-bottom:16px;}
    .hosp-name{font-family:'Libre Baskerville',Georgia,serif;font-size:18px;font-weight:700;color:#1e3a5f;letter-spacing:.8px;}
    .hosp-sub{font-size:11px;color:#374151;margin-top:2px;}
    .form-title{font-family:'Libre Baskerville',Georgia,serif;font-size:15px;font-weight:700;color:#1e3a5f;text-transform:uppercase;letter-spacing:2px;border:2px solid #1e3a5f;display:inline-block;padding:4px 28px;margin-top:10px;}
    .info-bar{display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #c8cdd5;margin-bottom:14px;}
    .info-cell{padding:6px 10px;font-size:11px;border-right:1px solid #c8cdd5;}
    .info-cell:nth-child(2n){border-right:none;}
    .info-cell:nth-child(3),.info-cell:nth-child(4){border-top:1px solid #c8cdd5;}
    .ilb{font-size:9.5px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;color:#374151;display:block;margin-bottom:1px;}
    .ival{font-weight:700;color:#1e3a5f;}
    .consent-block{margin-bottom:14px;}
    .consent-block p{font-size:12.5px;line-height:1.78;color:#111;text-align:justify;margin-bottom:10px;}
    .fi{display:inline;border-bottom:1px solid #111;padding:0 3px;font-weight:700;color:#1e3a5f;font-size:12.5px;white-space:nowrap;}
    .fin-box{background:#f4f6f9;border:1px solid #c8cdd5;padding:10px 13px;margin-bottom:14px;}
    .fin-box p{font-size:12px;line-height:1.68;color:#111;text-align:justify;}
    .fin-box p+p{margin-top:7px;}
    .pt-box{border:1.5px solid #1e3a5f;margin:16px 0;display:grid;grid-template-columns:1fr 1fr;gap:0;}
    .pt-cell{padding:7px 11px;border-right:1px solid #c8cdd5;}
    .pt-cell:nth-child(2n){border-right:none;}
    .pt-cell:nth-child(3),.pt-cell:nth-child(4){border-top:1px solid #c8cdd5;}
    .pt-lb{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;color:#374151;display:block;margin-bottom:2px;}
    .pt-val{font-size:12.5px;font-weight:700;color:#1e3a5f;}
    .sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:0;border-top:1px solid #c8cdd5;padding-top:16px;margin-top:20px;}
    .sig-col{padding-right:24px;}
    .sig-col:last-child{padding-right:0;padding-left:24px;border-left:1px solid #c8cdd5;}
    .sig-area{height:52px;border-bottom:1.5px solid #111;margin-bottom:8px;}
    .sig-primary{font-size:10.5px;font-weight:700;color:#374151;text-transform:uppercase;letter-spacing:.3px;}
    .sig-field{margin-top:7px;display:flex;align-items:baseline;gap:4px;font-size:11px;color:#374151;}
    .sig-field-line{flex:1;border-bottom:1px solid #111;min-height:15px;}
    .prefill{color:#1e3a5f;font-weight:700;font-size:11.5px;}
    .foot-note{margin-top:16px;border-top:1px solid #c8cdd5;padding-top:8px;font-size:10px;color:#374151;text-align:center;line-height:1.5;}
    .proc-bar{background:#f4f6f9;border:1px solid #c8cdd5;border-top:none;padding:5px 11px;font-size:10.5px;color:#374151;display:flex;flex-wrap:wrap;gap:12px;max-width:740px;margin:0 auto;}
    @media print{body{background:white;padding:0;}.page{box-shadow:none;padding:16px 22px;}}
    </style></head><body>
    <div class="page">
    <div class="hdr">
        <div class="hosp-name">CITY HOSPITAL</div>
        <div class="hosp-sub">123 Hospital Road, Karachi &nbsp;|&nbsp; Tel: 021-1234567</div>
        <div class="form-title">Consent Form</div>
        <div style="font-size:10px;color:#888;margin-top:5px;letter-spacing:.3px">Ref: ${a.admission_number||'—'}</div>
    </div>
    <div class="info-bar">
        <div class="info-cell"><span class="ilb">Admission No.</span><span class="ival">${a.admission_number||'—'}</span></div>
        <div class="info-cell"><span class="ilb">MRN</span><span class="ival">${a.mrn||'—'}</span></div>
        <div class="info-cell"><span class="ilb">Ward / Bed</span><span class="ival">${a.ward_name||'—'} / ${a.bed_number||'—'}</span></div>
        <div class="info-cell"><span class="ilb">Date of Admission</span><span class="ival">${admDate}</span></div>
    </div>
    <div class="consent-block">
        <p>I, <span class="fi">${guardian}</span>, &nbsp;S/O, W/O, D/O &nbsp;<span class="fi">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>,
        bearing CNIC No. <span class="fi">${cnic||'&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;'}</span>, being the patient / parent / guardian / family member
        of &nbsp;<span class="fi">${ptName}</span>, hereby give my full consent and authorize the
        physician / consultant / medical team in charge at this Hospital to carry out all necessary examination,
        diagnosis, treatment, procedures, medicines, and management required for the patient's condition.</p>
        <p>I understand that the disease, treatment, procedures, medicines, and hospital stay may involve certain known
        or unforeseen risks and complications. The treating team has the right to take necessary medical decisions in
        the best interest of the patient. I also confirm that I have understood the treatment plan explained to me
        to the best of my knowledge.</p>
        <p>I further authorize the Hospital to maintain and use the patient's clinical and treatment data for medical,
        audit, academic, and research purposes, while ensuring that confidentiality and privacy are not compromised.</p>
        <p>I also understand and agree that admission may be managed on an extra bed or temporary arrangement in case
        a regular bed is not immediately available, and I consent to the same.</p>
    </div>
    <div class="fin-box">
        <p><strong>Financial Consent:</strong> I agree to pay all hospital charges as per the approved tariff, including room
        charges, doctor fees, medication, investigations, and other applicable charges. I understand that estimates
        shared at admission are approximate and final billing will be done at discharge. In case of insurance, any
        uncovered amount will be paid by me personally.</p>
        <p><strong>Advance Paid: ${advancePaid}</strong> &nbsp;|&nbsp; Receipt No: ${receiptNo} &nbsp;|&nbsp; Payment Mode: ${a.payment_type||'Cash'}</p>
    </div>
    <div class="pt-box">
        <div class="pt-cell"><span class="pt-lb">Patient Name</span><span class="pt-val">${ptName}</span></div>
        <div class="pt-cell"><span class="pt-lb">MR No.</span><span class="pt-val">${a.mrn||'—'}</span></div>
        <div class="pt-cell"><span class="pt-lb">IPD No.</span><span class="pt-val">${a.admission_number||'—'}</span></div>
        <div class="pt-cell"><span class="pt-lb">Ward / Bed</span><span class="pt-val">${a.ward_name||'—'} &nbsp;/&nbsp; ${a.bed_number||'—'}</span></div>
    </div>
    <div class="sig-grid">
        <div class="sig-col">
            <div class="sig-area"></div>
            <div class="sig-primary">Signature / Thumb Impression of Family Member / Attendant</div>
            <div class="sig-field">Name of Signatory: <span class="sig-field-line prefill">${a.next_of_kin_name||''}</span></div>
            <div class="sig-field">Relation with Patient: <span class="sig-field-line prefill">${a.next_of_kin_relation||''}</span></div>
            <div class="sig-field">CNIC No: <span class="sig-field-line prefill">${cnic}</span></div>
            <div class="sig-field">Date &amp; Time: <span class="sig-field-line"></span></div>
        </div>
        <div class="sig-col">
            <div class="sig-area"></div>
            <div class="sig-primary">Witness / Staff Signature</div>
            <div class="sig-field">Name: <span class="sig-field-line"></span></div>
            <div class="sig-field">Designation: <span class="sig-field-line"></span></div>
            <div class="sig-field">Date &amp; Time: <span class="sig-field-line"></span></div>
        </div>
    </div>
    <div class="foot-note">This consent form confirms that the undersigned has read / understood the contents and has signed voluntarily.</div>
    </div>
    <div class="proc-bar"><span><strong>Processed On:</strong> ${now}</span></div>
    <script>window.onload=()=>{window.print();}</script></body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    w.document.write(html); w.document.close();
};

export default function IPDPatient() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState(location.state?.tab || 'vitals');
    const [loading, setLoading] = useState(true);
    const [showDischarge, setShowDischarge] = useState(false);
    const [dischargeForm, setDischargeForm] = useState({
        discharge_type: 'Improved',
        diagnosis: '',
        presenting_complaints: '',
        hospital_course: '',
        relevant_investigations: '',
        surgery_date: '',
        operative_procedure: '',
        pending_reports: '',
        discharge_instructions: '',
        medication_on_discharge: '',
        follow_up_date: '',
        follow_up_department: '',
        condition_on_discharge: 'Improved',
        additional_comments: '',
        discharge_summary: '',
    });
    const [showFinalBill, setShowFinalBill] = useState(false);
    const [finalBillData, setFinalBillData] = useState(null);
    const [billAdv, setBillAdv] = useState(0);
    const [billDisc, setBillDisc] = useState(0);
    const [billOther, setBillOther] = useState(0);
    const [billOtherDesc, setBillOtherDesc] = useState('');
    const [billMode, setBillMode] = useState('Cash');
    const [billSaving, setBillSaving] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [wards, setWards] = useState([]);
    const [beds, setBeds] = useState([]);

    // Forms
    const [vitalsForm, setVitalsForm] = useState({ temperature: '', pulse: '', bp_systolic: '', bp_diastolic: '', respiratory_rate: '', oxygen_saturation: '', weight: '', height: '', notes: '' });
    const [noteForm, setNoteForm] = useState({ subjective: '', objective: '', assessment: '', plan: '', note_type: 'Progress' });
    const [medForm, setMedForm] = useState({ medicine_name: '', dose: '', route: 'Oral', frequency: '', instructions: '' });
    const [saving, setSaving] = useState(false);
    const [returnModal, setReturnModal] = useState(null);
    const [returnQty, setReturnQty] = useState('1');
    const [returnReason, setReturnReason] = useState('');

    const load = () => {
        setLoading(true);
        ipdAPI.getAdmission(id).then(r => {
            setData(r.data);
            const a = r.data.admission;
            setEditForm({ ward_id: a.ward_id, bed_id: a.bed_id, admitting_doctor: a.admitting_doctor||'', department: a.department||'', admission_purpose: a.admission_purpose||'', diagnosis: a.diagnosis||'' });
        }).catch(() => navigate('/ipd')).finally(() => setLoading(false));
    };

    const openEdit = () => {
        ipdAPI.getWards().then(r => setWards(r.data)).catch(() => {});
        if (data?.admission?.ward_id) ipdAPI.getWardBeds(data.admission.ward_id).then(r => setBeds(r.data)).catch(() => {});
        setShowEdit(true);
    };

    const onEditWardChange = (ward_id) => {
        setEditForm(f => ({ ...f, ward_id, bed_id: '' }));
        ipdAPI.getWardBeds(ward_id).then(r => setBeds(r.data)).catch(() => {});
    };

    const saveEdit = async () => {
        setSaving(true);
        try {
            await ipdAPI.updateAdmission(id, editForm);
            toast.success('Admission details updated');
            setShowEdit(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.message || 'Update failed');
        } finally { setSaving(false); }
    };

    useEffect(() => { load(); }, [id]);

    const saveVitals = async () => {
        setSaving(true);
        try {
            await ipdAPI.addVitals({ admission_id: parseInt(id), ...vitalsForm });
            toast.success('Vitals recorded');
            setVitalsForm({ temperature: '', pulse: '', bp_systolic: '', bp_diastolic: '', respiratory_rate: '', oxygen_saturation: '', weight: '', height: '', notes: '' });
            load();
        } catch { toast.error('Failed'); } finally { setSaving(false); }
    };

    const saveNote = async () => {
        if (!noteForm.subjective && !noteForm.assessment) return toast.error('Please write at least a subjective or assessment note');
        setSaving(true);
        try {
            await ipdAPI.addNote({ admission_id: parseInt(id), ...noteForm });
            toast.success('Note added');
            setNoteForm({ subjective: '', objective: '', assessment: '', plan: '', note_type: 'Progress' });
            load();
        } catch { toast.error('Failed'); } finally { setSaving(false); }
    };

    const saveMed = async () => {
        if (!medForm.medicine_name) return toast.error('Medicine name is required');
        setSaving(true);
        try {
            await ipdAPI.addMedication({ admission_id: parseInt(id), ...medForm });
            toast.success('Medication added');
            setMedForm({ medicine_name: '', dose: '', route: 'Oral', frequency: '', instructions: '' });
            load();
        } catch { toast.error('Failed'); } finally { setSaving(false); }
    };

    const openDischargeModal = () => {
        const a = data?.admission || {};
        const meds = (data?.medications || []).filter(m => m.status !== 'Stopped');
        const medText = meds.map(m => `${m.medicine_name}${m.dose ? ' ' + m.dose : ''}${m.frequency ? ' — ' + m.frequency : ''}${m.route ? ' (' + m.route + ')' : ''}`).join('\n');
        let saved = {};
        try { const s = localStorage.getItem(`discharge_draft_${id}`); if (s) saved = JSON.parse(s); } catch {}
        setDischargeForm(f => ({
            ...f,
            diagnosis: a.diagnosis || '',
            medication_on_discharge: medText,
            ...saved,
        }));
        setShowDischarge(true);
    };

    const saveDischargeDraft = () => {
        try { localStorage.setItem(`discharge_draft_${id}`, JSON.stringify(dischargeForm)); } catch {}
        toast.success('Draft saved');
    };

    const discharge = async () => {
        if (!dischargeForm.condition_on_discharge) return toast.error('Condition on discharge is required');
        const payload = {
            ...dischargeForm,
            discharge_summary: [
                dischargeForm.diagnosis && `Diagnosis: ${dischargeForm.diagnosis}`,
                dischargeForm.presenting_complaints && `Presenting Complaints: ${dischargeForm.presenting_complaints}`,
                dischargeForm.hospital_course && `Hospital Course: ${dischargeForm.hospital_course}`,
                dischargeForm.operative_procedure && `Operative Procedure: ${dischargeForm.operative_procedure}`,
                dischargeForm.discharge_instructions && `Instructions: ${dischargeForm.discharge_instructions}`,
                dischargeForm.condition_on_discharge && `Condition: ${dischargeForm.condition_on_discharge}`,
                dischargeForm.additional_comments && `Comments: ${dischargeForm.additional_comments}`,
            ].filter(Boolean).join('\n'),
        };
        setSaving(true);
        try {
            await ipdAPI.dischargePatient(id, payload);
            setShowDischarge(false);
            // Load final bill after discharge
            const billR = await ipdBillingAPI.calculateBill(parseInt(id));
            const bd = billR.data;
            setFinalBillData(bd);
            setBillAdv(bd.totals.advance_paid || 0);
            setBillDisc(bd.totals.discount_pct || 0);
            setBillOther(bd.charges.other_charges || 0);
            setBillOtherDesc(bd.charges.other_charges_desc || '');
            setBillMode('Cash');
            setShowFinalBill(true);
            load();
        } catch { toast.error('Failed to discharge'); } finally { setSaving(false); }
    };

    const fmtBill = (n) => parseFloat(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const numB = (v) => parseFloat(v) || 0;

    const finalTotals = () => {
        if (!finalBillData) return {};
        const c = finalBillData.charges;
        const subtotalBefore = numB(c.room_charges) + numB(c.consult_charges) + numB(c.lab_charges) +
            numB(c.radiology_charges) + numB(c.pharmacy_charges) + numB(c.procedure_charges) + numB(billOther);
        const discAmt = +(subtotalBefore * numB(billDisc) / 100).toFixed(2);
        const subtotal = +(subtotalBefore - discAmt).toFixed(2);
        const balance = +(subtotal - numB(billAdv)).toFixed(2);
        return { subtotalBefore, discAmt, subtotal, balance };
    };

    const saveFinalBill = async (andPrint = false) => {
        if (!finalBillData) return;
        setBillSaving(true);
        try {
            const t = finalTotals();
            const c = finalBillData.charges;
            const a = finalBillData.admission;
            await ipdBillingAPI.saveBill({
                admission_id: finalBillData.admission.id,
                patient_id: null,
                room_days: c.days, room_rate: c.ward_rate, room_charges: c.room_charges,
                consult_charges: c.consult_charges,
                lab_charges: c.lab_charges, radiology_charges: c.radiology_charges,
                pharmacy_charges: c.pharmacy_charges, procedure_charges: c.procedure_charges,
                other_charges: numB(billOther), other_charges_desc: billOtherDesc,
                discount_pct: numB(billDisc), discount_amount: t.discAmt,
                subtotal: t.subtotal, advance_paid: numB(billAdv), balance_due: t.balance,
                payment_mode: billMode, payment_status: t.balance <= 0 ? 'Paid' : 'Pending', notes: '',
            });
            if (andPrint) printFinalBill(a, c, t);
            toast.success('Final bill saved');
            setShowFinalBill(false);
        } catch { toast.error('Failed to save bill'); } finally { setBillSaving(false); }
    };

    const printFinalBill = (a, c, t) => {
        const fmt = (n) => parseFloat(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>IPD Bill — ${a.admission_number}</title>
<style>
  body{font-family:Arial,sans-serif;font-size:12px;margin:24px;color:#111}
  h2{text-align:center;margin:0;font-size:18px;color:#1e3a5f}
  .sub{text-align:center;color:#555;margin-bottom:16px;font-size:11px}
  .patient-box{background:#f0f4f8;padding:10px 14px;border-radius:6px;margin-bottom:16px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;font-size:11px}
  .patient-box b{color:#555}
  .section{font-weight:bold;background:#1e3a5f;color:#fff;padding:5px 10px;margin:14px 0 6px;border-radius:4px;font-size:12px}
  table{width:100%;border-collapse:collapse}
  td,th{padding:5px 8px;border-bottom:1px solid #e5e7eb;font-size:12px}
  th{background:#f8fafc;font-weight:700;text-align:left;color:#374151}
  .right{text-align:right}
  .total-row td{font-weight:700;background:#f0fdf4;color:#065f46;font-size:13px}
  .balance-row td{font-weight:800;background:#1e3a5f;color:#fff;font-size:14px}
  .footer{margin-top:24px;font-size:10px;color:#999;text-align:center;border-top:1px solid #e5e7eb;padding-top:8px}
</style></head><body>
<h2>IPD Bill / Discharge Invoice</h2>
<div class="sub">Generated: ${new Date().toLocaleString('en-PK')} | Bill Status: Pending</div>
<div class="patient-box">
  <div><b>Patient:</b> ${a.patient_name || '—'}</div>
  <div><b>MR#:</b> ${a.mrn || '—'}</div>
  <div><b>Admission#:</b> ${a.admission_number || '—'}</div>
  <div><b>Age/Gender:</b> ${a.age_years || '-'} yrs / ${a.gender || '-'}</div>
  <div><b>Ward:</b> ${a.ward_name || '-'}</div>
  <div><b>Consultant:</b> ${a.consultant || '-'}</div>
  <div><b>Admitted:</b> ${a.admission_time ? new Date(a.admission_time).toLocaleDateString('en-PK') : '-'}</div>
  <div><b>Discharged:</b> ${a.discharge_time ? new Date(a.discharge_time).toLocaleDateString('en-PK') : new Date().toLocaleDateString('en-PK')}</div>
  <div><b>Duration:</b> ${c.days} day(s)</div>
</div>
<div class="section">Charge Details</div>
<table>
  <tr><th>Description</th><th>Rate</th><th>Days/Qty</th><th class="right">Amount (Rs.)</th></tr>
  <tr><td>Room / Ward Charges</td><td>Rs. ${fmt(c.ward_rate)}/day</td><td>${c.days}</td><td class="right">${fmt(c.room_charges)}</td></tr>
  <tr><td>Consultant Fee (${a.consultant || '-'})</td><td>Rs. ${fmt(c.consult_rate)}/day</td><td>${c.days}</td><td class="right">${fmt(c.consult_charges)}</td></tr>
  <tr><td>Laboratory Charges</td><td>—</td><td>—</td><td class="right">${fmt(c.lab_charges)}</td></tr>
  <tr><td>Radiology / Imaging</td><td>—</td><td>—</td><td class="right">${fmt(c.radiology_charges)}</td></tr>
  <tr><td>Pharmacy / Medicines</td><td>—</td><td>—</td><td class="right">${fmt(c.pharmacy_charges)}</td></tr>
  <tr><td>Procedures</td><td>—</td><td>—</td><td class="right">${fmt(c.procedure_charges)}</td></tr>
  ${numB(billOther) > 0 ? `<tr><td>Other${billOtherDesc ? ': '+billOtherDesc : ''}</td><td>—</td><td>—</td><td class="right">${fmt(numB(billOther))}</td></tr>` : ''}
  <tr><td colspan="3" style="font-weight:700">Sub-Total (Before Discount)</td><td class="right" style="font-weight:700">${fmt(t.subtotalBefore)}</td></tr>
  <tr><td colspan="3">Discount (${billDisc}%)</td><td class="right" style="color:#dc2626">- ${fmt(t.discAmt)}</td></tr>
  <tr class="total-row"><td colspan="3">Net Total</td><td class="right">${fmt(t.subtotal)}</td></tr>
  <tr><td colspan="3">Advance Paid</td><td class="right" style="color:#059669">- ${fmt(numB(billAdv))}</td></tr>
  <tr class="balance-row"><td colspan="3">Balance Due</td><td class="right">Rs. ${fmt(t.balance)}</td></tr>
</table>
<p style="font-size:11px;margin-top:8px"><b>Payment Mode:</b> ${billMode} &nbsp;|&nbsp; <b>Status:</b> Pending</p>
<div class="footer">HIMS — Hospital Information Management System &nbsp;|&nbsp; This is a computer generated invoice</div>
<script>window.onload=()=>{window.print();}</script>
</body></html>`;
        const w = window.open('', '_blank', 'width=900,height=700');
        if (w) { w.document.write(html); w.document.close(); }
    };

    if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div>;
    if (!data) return null;

    const { admission, notes, vitals, medications } = data;

    const tabs = [
        { key: 'vitals', label: `Vitals (${vitals.length})` },
        { key: 'notes', label: `Progress Notes (${notes.length})` },
        { key: 'medications', label: `Medications (${medications.length})` },
    ];

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <button onClick={() => navigate('/ipd')}
                    style={{ padding: '7px 14px', background: '#f1f5f9', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>← Back</button>
                <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e3a5f', margin: 0 }}>IPD Patient</h2>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={() => printConsentForm(admission)}
                        style={{ padding: '8px 14px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        Print Consent Form
                    </button>
                    <button onClick={() => printAdmissionSlip(admission)}
                        style={{ padding: '8px 14px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        Print Admission Slip
                    </button>
                    {admission.status === 'Admitted' && (
                        <button onClick={openEdit}
                            style={{ padding: '8px 14px', background: '#b45309', color: 'white', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                            Edit Details
                        </button>
                    )}
                    {admission.status === 'Discharged' && (
                        <button onClick={() => printDischargeSlip(admission)}
                            style={{ padding: '8px 16px', background: '#059669', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            Print Discharge Slip
                        </button>
                    )}
                    {admission.status === 'Admitted' && (
                        <button onClick={openDischargeModal}
                            style={{ padding: '8px 18px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            Discharge Patient
                        </button>
                    )}
                </div>
            </div>

            {/* Patient Card */}
            <div style={{ background: '#1e3a5f', borderRadius: 10, padding: 20, marginBottom: 16, color: 'white', display: 'flex', gap: 24, alignItems: 'center' }}>
                <div style={{ width: 56, height: 56, background: '#2d5a8e', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>🏥</div>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>{admission.full_name}</div>
                    <div style={{ fontSize: 13, color: '#93c5fd', marginTop: 4 }}>
                        MRN: <strong>{admission.mrn}</strong> | {admission.gender} | Age: {admission.age_years}y | Blood: {admission.blood_group || '—'}
                    </div>
                    <div style={{ fontSize: 12, color: '#93c5fd', marginTop: 2 }}>
                        <strong>{admission.admission_number}</strong> | Ward: {admission.ward_name} | Bed: <strong>{admission.bed_number}</strong> | Dr. {admission.admitting_doctor || '—'}
                    </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ background: admission.status === 'Admitted' ? '#059669' : '#dc2626', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 700 }}>{admission.status}</div>
                    <div style={{ fontSize: 11, color: '#93c5fd', marginTop: 6 }}>Since: {new Date(admission.admission_time).toLocaleDateString('en-PK')}</div>
                    <div style={{ fontSize: 12, color: '#fbbf24', marginTop: 2 }}>{admission.diagnosis || '—'}</div>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        style={{ padding: '9px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            background: tab === t.key ? '#1e3a5f' : '#f1f5f9',
                            color: tab === t.key ? 'white' : '#374151' }}>
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Vitals Tab */}
            {tab === 'vitals' && (
                <div>
                    {/* Add Vitals */}
                    <div style={{ background: 'white', borderRadius: 10, padding: 20, marginBottom: 14, border: '1px solid #e2e8f0' }}>
                        <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 12 }}>Record Vitals</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                            {[
                                { key: 'temperature', label: 'Temp (°C)', placeholder: '37.0' },
                                { key: 'pulse', label: 'Pulse (bpm)', placeholder: '80' },
                                { key: 'bp_systolic', label: 'BP Systolic', placeholder: '120' },
                                { key: 'bp_diastolic', label: 'BP Diastolic', placeholder: '80' },
                                { key: 'respiratory_rate', label: 'RR (/min)', placeholder: '16' },
                                { key: 'oxygen_saturation', label: 'SpO2 (%)', placeholder: '98' },
                                { key: 'weight', label: 'Weight (kg)', placeholder: '70' },
                                { key: 'height', label: 'Height (cm)', placeholder: '170' },
                            ].map(f => (
                                <div key={f.key}>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>{f.label}</label>
                                    <input type="number" style={inputStyle} value={vitalsForm[f.key]} onChange={e => setVitalsForm(v => ({ ...v, [f.key]: e.target.value }))} placeholder={f.placeholder} />
                                </div>
                            ))}
                            <div style={{ gridColumn: 'span 4' }}>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Notes</label>
                                <input style={inputStyle} value={vitalsForm.notes} onChange={e => setVitalsForm(v => ({ ...v, notes: e.target.value }))} placeholder="Any observations..." />
                            </div>
                        </div>
                        <button onClick={saveVitals} disabled={saving}
                            style={{ marginTop: 12, padding: '9px 24px', background: saving ? '#9ca3af' : '#1e3a5f', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            {saving ? 'Saving...' : '💾 Save Vitals'}
                        </button>
                    </div>

                    {/* Vitals History */}
                    {vitals.length > 0 && (
                        <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc' }}>
                                        {['Time', 'Temp', 'Pulse', 'BP', 'RR', 'SpO2', 'Wt', 'By'].map(h => (
                                            <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {vitals.map((v, i) => (
                                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '9px 12px', fontSize: 11 }}>{new Date(v.recorded_at).toLocaleString('en-PK', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 11 }}>{v.temperature ? `${v.temperature}°` : '—'}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 11 }}>{v.pulse || '—'}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 11, fontWeight: 600 }}>{v.bp_systolic && v.bp_diastolic ? `${v.bp_systolic}/${v.bp_diastolic}` : '—'}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 11 }}>{v.respiratory_rate || '—'}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 11, color: v.oxygen_saturation < 95 ? '#dc2626' : '#059669', fontWeight: 600 }}>{v.oxygen_saturation ? `${v.oxygen_saturation}%` : '—'}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 11 }}>{v.weight ? `${v.weight}kg` : '—'}</td>
                                            <td style={{ padding: '9px 12px', fontSize: 11, color: '#64748b' }}>{v.recorded_by_name || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Notes Tab */}
            {tab === 'notes' && (
                <div>
                    <div style={{ background: 'white', borderRadius: 10, padding: 20, marginBottom: 14, border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', margin: 0 }}>Add Progress Note</h3>
                            <select style={{ ...inputStyle, width: 'auto', background: 'white', fontSize: 12 }} value={noteForm.note_type} onChange={e => setNoteForm(n => ({ ...n, note_type: e.target.value }))}>
                                <option>Progress</option><option>Nursing</option><option>Consultant</option><option>Procedure</option>
                            </select>
                        </div>
                        {[
                            { key: 'subjective', label: 'Subjective (Patient complaints)', placeholder: 'Fever since 2 days, headache...' },
                            { key: 'objective', label: 'Objective (Examination findings)', placeholder: 'Temp 38.5, BP 130/80...' },
                            { key: 'assessment', label: 'Assessment (Diagnosis/Impression)', placeholder: 'Viral fever, improving...' },
                            { key: 'plan', label: 'Plan (Management)', placeholder: 'Continue antibiotics, repeat CBC tomorrow...' },
                        ].map(f => (
                            <div key={f.key} style={{ marginBottom: 10 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>{f.label}</label>
                                <textarea style={{ ...inputStyle, height: 50 }} value={noteForm[f.key]} onChange={e => setNoteForm(n => ({ ...n, [f.key]: e.target.value }))} placeholder={f.placeholder} />
                            </div>
                        ))}
                        <button onClick={saveNote} disabled={saving}
                            style={{ padding: '9px 24px', background: saving ? '#9ca3af' : '#1e3a5f', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            {saving ? 'Saving...' : '📝 Add Note'}
                        </button>
                    </div>

                    {notes.map((n, i) => (
                        <div key={i} style={{ background: 'white', borderRadius: 10, padding: 16, marginBottom: 10, border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                                <span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>{n.note_type}</span>
                                <span style={{ fontSize: 11, color: '#64748b' }}>{new Date(n.note_time).toLocaleString('en-PK')} | {n.written_by_name}</span>
                            </div>
                            {n.subjective && <div style={{ marginBottom: 6 }}><span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>S: </span><span style={{ fontSize: 12 }}>{n.subjective}</span></div>}
                            {n.objective && <div style={{ marginBottom: 6 }}><span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>O: </span><span style={{ fontSize: 12 }}>{n.objective}</span></div>}
                            {n.assessment && <div style={{ marginBottom: 6 }}><span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>A: </span><span style={{ fontSize: 12 }}>{n.assessment}</span></div>}
                            {n.plan && <div><span style={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>P: </span><span style={{ fontSize: 12 }}>{n.plan}</span></div>}
                        </div>
                    ))}
                </div>
            )}

            {/* Medications Tab */}
            {tab === 'medications' && (
                <div>
                    <div style={{ background: 'white', borderRadius: 10, padding: 20, marginBottom: 14, border: '1px solid #e2e8f0' }}>
                        <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 12 }}>Add Medication</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 2fr', gap: 10 }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Medicine Name *</label>
                                <input style={inputStyle} value={medForm.medicine_name} onChange={e => setMedForm(m => ({ ...m, medicine_name: e.target.value }))} placeholder="e.g. Augmentin" />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Dose</label>
                                <input style={inputStyle} value={medForm.dose} onChange={e => setMedForm(m => ({ ...m, dose: e.target.value }))} placeholder="e.g. 625mg" />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Route</label>
                                <select style={{ ...inputStyle, background: 'white' }} value={medForm.route} onChange={e => setMedForm(m => ({ ...m, route: e.target.value }))}>
                                    <option>Oral</option><option>IV</option><option>IM</option><option>SC</option><option>Topical</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Frequency</label>
                                <input style={inputStyle} value={medForm.frequency} onChange={e => setMedForm(m => ({ ...m, frequency: e.target.value }))} placeholder="e.g. BD, TDS, OD" />
                            </div>
                            <div style={{ gridColumn: 'span 4' }}>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3 }}>Instructions</label>
                                <input style={inputStyle} value={medForm.instructions} onChange={e => setMedForm(m => ({ ...m, instructions: e.target.value }))} placeholder="e.g. After meals, with water" />
                            </div>
                        </div>
                        <button onClick={saveMed} disabled={saving}
                            style={{ marginTop: 12, padding: '9px 24px', background: saving ? '#9ca3af' : '#059669', color: 'white', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            {saving ? 'Saving...' : '💊 Add Medication'}
                        </button>
                    </div>

                    {medications.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 30, color: '#64748b', background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>No medications prescribed</div>
                    ) : (
                        <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc' }}>
                                        {['Medicine', 'Dose', 'Route', 'Frequency', 'Instructions', 'By', 'Actions'].map(h => (
                                            <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {medications.map((m, i) => (
                                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '9px 14px', fontSize: 13, fontWeight: 700, color: '#059669' }}>{m.medicine_name}</td>
                                            <td style={{ padding: '9px 14px', fontSize: 12 }}>{m.dose || '—'}</td>
                                            <td style={{ padding: '9px 14px' }}><span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{m.route}</span></td>
                                            <td style={{ padding: '9px 14px', fontSize: 12, fontWeight: 600 }}>{m.frequency || '—'}</td>
                                            <td style={{ padding: '9px 14px', fontSize: 11, color: '#64748b' }}>{m.instructions || '—'}</td>
                                            <td style={{ padding: '9px 14px', fontSize: 11, color: '#64748b' }}>{m.prescribed_by_name || '—'}</td>
                                            <td style={{ padding: '9px 14px' }}>
                                                {admission?.status === 'Admitted' && (
                                                    <button type="button"
                                                        onClick={() => { setReturnModal(m); setReturnQty('1'); setReturnReason(''); }}
                                                        style={{ padding: '3px 10px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                        Return
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Return Medicine Modal */}
            {returnModal && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <div style={{ background:'white', borderRadius:12, padding:24, width:400, maxWidth:'95vw' }}>
                        <h3 style={{ margin:'0 0 4px', color:'#b45309', fontSize:16 }}>Return Medicine to Pharmacy</h3>
                        <p style={{ margin:'0 0 16px', color:'#6b7280', fontSize:12 }}><strong>{returnModal.medicine_name}</strong> — {returnModal.dose || ''}</p>
                        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                            <div>
                                <label style={{ fontSize:11, fontWeight:600, display:'block', marginBottom:3 }}>Quantity to Return</label>
                                <input type="number" min="1" value={returnQty} onChange={e=>setReturnQty(e.target.value)}
                                    style={{ width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, boxSizing:'border-box' }} />
                            </div>
                            <div>
                                <label style={{ fontSize:11, fontWeight:600, display:'block', marginBottom:3 }}>Reason</label>
                                <input placeholder="e.g. Discontinued, Patient discharged..." value={returnReason} onChange={e=>setReturnReason(e.target.value)}
                                    style={{ width:'100%', padding:'7px 10px', border:'1px solid #d1d5db', borderRadius:6, fontSize:13, boxSizing:'border-box' }} />
                            </div>
                        </div>
                        <div style={{ display:'flex', gap:10, marginTop:20 }}>
                            <button type="button" disabled={saving}
                                onClick={async () => {
                                    setSaving(true);
                                    try {
                                        await pharmacyWardAPI.createWardReturn({
                                            admission_id: parseInt(id),
                                            ward_id: admission?.ward_id,
                                            medicine_name: returnModal.medicine_name,
                                            returned_qty: parseInt(returnQty) || 1,
                                            reason: returnReason,
                                            request_id: returnModal.pwr_id || null,
                                        });
                                        toast.success('Return request sent to pharmacy');
                                        setReturnModal(null);
                                    } catch(e) {
                                        toast.error(e?.response?.data?.message || 'Failed to send return');
                                    } finally { setSaving(false); }
                                }}
                                style={{ flex:1, padding:'10px', background: saving ? '#9ca3af':'#b45309', color:'white', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor: saving?'not-allowed':'pointer' }}>
                                {saving ? 'Sending...' : 'Send Return Request'}
                            </button>
                            <button type="button" onClick={()=>setReturnModal(null)}
                                style={{ flex:1, padding:'10px', background:'#f1f5f9', color:'#374151', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer' }}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Admission Modal */}
            {showEdit && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', borderRadius: 12, padding: 28, width: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#b45309', marginBottom: 16 }}>Edit Admission Details</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Ward</label>
                                <select style={{ ...inputStyle, background: 'white' }} value={editForm.ward_id||''} onChange={e => onEditWardChange(e.target.value)}>
                                    <option value="">— Select Ward —</option>
                                    {wards.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Bed</label>
                                <select style={{ ...inputStyle, background: 'white' }} value={editForm.bed_id||''} onChange={e => setEditForm(f => ({ ...f, bed_id: e.target.value }))}>
                                    <option value="">— Select Bed —</option>
                                    {beds.map(b => <option key={b.id} value={b.id} disabled={b.status==='Occupied' && String(b.id)!==String(editForm.bed_id)}>{b.bed_number} {b.status==='Occupied' && String(b.id)!==String(editForm.bed_id) ? '(Occupied)' : ''}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Department</label>
                                <input style={inputStyle} value={editForm.department} onChange={e => setEditForm(f => ({ ...f, department: e.target.value }))} placeholder="e.g. General Medicine" />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Admitting Doctor</label>
                                <input style={inputStyle} value={editForm.admitting_doctor} onChange={e => setEditForm(f => ({ ...f, admitting_doctor: e.target.value }))} placeholder="Dr. Name" />
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Admission Purpose</label>
                                <select style={{ ...inputStyle, background: 'white' }} value={editForm.admission_purpose} onChange={e => setEditForm(f => ({ ...f, admission_purpose: e.target.value }))}>
                                    <option value="">— Select —</option>
                                    {['Elective','Emergency','Maternity','Daycare Surgery','Observation'].map(o => <option key={o}>{o}</option>)}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Diagnosis</label>
                                <input style={inputStyle} value={editForm.diagnosis} onChange={e => setEditForm(f => ({ ...f, diagnosis: e.target.value }))} placeholder="Provisional diagnosis" />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                            <button onClick={() => setShowEdit(false)} style={{ padding: '9px 20px', background: '#f1f5f9', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
                            <button onClick={saveEdit} disabled={saving} style={{ padding: '9px 20px', background: '#b45309', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Discharge Modal */}
            {showDischarge && (() => {
                const df = dischargeForm;
                const setDF = (k, v) => setDischargeForm(f => ({ ...f, [k]: v }));
                const secBar = (title) => (
                    <div style={{ background: '#1e3a5f', color: 'white', fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 4, marginBottom: 10, marginTop: 14, letterSpacing: '.5px', textTransform: 'uppercase' }}>{title}</div>
                );
                const field = (label, key, type = 'textarea', opts = null) => (
                    <div style={{ marginBottom: 10 }} key={key}>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3, color: '#374151' }}>{label}</label>
                        {type === 'select' ? (
                            <select style={{ ...inputStyle, background: 'white' }} value={df[key]} onChange={e => setDF(key, e.target.value)}>
                                {opts.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        ) : type === 'date' ? (
                            <input type="date" style={inputStyle} value={df[key]} onChange={e => setDF(key, e.target.value)} />
                        ) : type === 'text' ? (
                            <input type="text" style={inputStyle} value={df[key]} onChange={e => setDF(key, e.target.value)} />
                        ) : (
                            <textarea style={{ ...inputStyle, height: 72 }} value={df[key]} onChange={e => setDF(key, e.target.value)} />
                        )}
                    </div>
                );
                return (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
                        <div style={{ background: 'white', borderRadius: 12, padding: '24px 28px', width: 740, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                                <div>
                                    <h3 style={{ fontSize: 17, fontWeight: 800, color: '#dc2626', margin: 0 }}>Discharge Summary</h3>
                                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{data?.admission?.full_name} &nbsp;|&nbsp; {data?.admission?.admission_number}</div>
                                </div>
                                <button onClick={() => setShowDischarge(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>✕ Close</button>
                            </div>

                            {/* Condition on discharge — top prominent */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 4 }}>
                                {field('Condition on Discharge *', 'condition_on_discharge', 'select', ['Improved', 'LAMA', 'Referred', 'Expired', 'Other'])}
                                {field('Discharge Type', 'discharge_type', 'select', ['Improved', 'DAMA (Against Medical Advice)', 'Transfer', 'Death', 'Referred'])}
                            </div>

                            {secBar('Clinical Information')}
                            {field('Diagnosis', 'diagnosis', 'textarea')}
                            {field('Presenting Complaints', 'presenting_complaints', 'textarea')}
                            {field('Hospital Course', 'hospital_course', 'textarea')}
                            {field('Relevant Investigations', 'relevant_investigations', 'textarea')}

                            {secBar('Surgical Information')}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                {field('Date of Surgery', 'surgery_date', 'date')}
                                <div />
                            </div>
                            {field('Operative Procedure', 'operative_procedure', 'textarea')}
                            {field('Pending Reports', 'pending_reports', 'text')}

                            {secBar('Discharge Instructions')}
                            {field('Instructions on Discharge', 'discharge_instructions', 'textarea')}
                            <div>
                                <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 3, color: '#374151' }}>
                                    Medication on Discharge <span style={{ fontWeight: 400, color: '#6b7280' }}>(auto-filled from active medications — edit if needed)</span>
                                </label>
                                <textarea style={{ ...inputStyle, height: 90, fontFamily: 'monospace', fontSize: 12 }} value={df.medication_on_discharge} onChange={e => setDF('medication_on_discharge', e.target.value)} />
                            </div>

                            {secBar('Follow-up')}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                {field('Follow-up Date', 'follow_up_date', 'date')}
                                {field('Follow-up Department / Doctor', 'follow_up_department', 'text')}
                            </div>

                            {secBar('Additional Comments')}
                            {field('Additional Comments / Summary', 'additional_comments', 'textarea')}

                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                                <button onClick={() => setShowDischarge(false)} style={{ padding: '9px 20px', background: '#f1f5f9', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Cancel</button>
                                <button onClick={saveDischargeDraft} style={{ padding: '9px 20px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>💾 Save</button>
                                <button
                                    onClick={() => {
                                        const a = data?.admission || {};
                                        const meds = (data?.medications || []).filter(m => m.status !== 'Stopped');
                                        printIPDDischargeSummary(a, dischargeForm, meds, data?.lab_orders || [], data?.radiology_orders || []);
                                    }}
                                    style={{ padding: '9px 20px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                    Preview Print
                                </button>
                                <button onClick={discharge} disabled={saving} style={{ padding: '9px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                    {saving ? 'Discharging...' : 'Confirm Discharge & Print'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Final Bill Modal */}
            {showFinalBill && finalBillData && (() => {
                const t = finalTotals();
                const c = finalBillData.charges;
                const a = finalBillData.admission;
                const inpS = { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' };
                const row = (label, val, sub) => (
                    <div key={label} style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 8, padding: '5px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <span style={{ fontSize: 13, color: sub ? '#6b7280' : '#374151', paddingLeft: sub ? 12 : 0 }}>{label}</span>
                        <span style={{ textAlign: 'right', fontSize: 13, fontWeight: 600 }}>Rs. {fmtBill(val)}</span>
                    </div>
                );
                return (
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, overflowY: 'auto', padding: 20 }}>
                        <div style={{ background: 'white', borderRadius: 14, padding: 28, width: 640, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                                <div>
                                    <h3 style={{ fontSize: 17, fontWeight: 800, color: '#1e3a5f', margin: 0 }}>Final Discharge Bill</h3>
                                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{a.patient_name} &nbsp;|&nbsp; {a.admission_number} &nbsp;|&nbsp; {c.days} day(s)</div>
                                </div>
                                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '4px 12px', fontSize: 12, fontWeight: 700, color: '#dc2626' }}>DISCHARGED</div>
                            </div>

                            {/* Charge Breakdown */}
                            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>Charge Breakdown</div>
                                {row(`Room/Ward (Rs.${fmtBill(c.ward_rate)}/day × ${c.days} days)`, c.room_charges)}
                                {row(`Consultant (Rs.${fmtBill(c.consult_rate)}/day × ${c.days} days)`, c.consult_charges)}
                                {row('Laboratory', c.lab_charges, true)}
                                {row('Radiology / Imaging', c.radiology_charges, true)}
                                {row('Pharmacy / Medicines', c.pharmacy_charges, true)}
                                {row('Procedures', c.procedure_charges, true)}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 8, padding: '6px 0', alignItems: 'center' }}>
                                    <input value={billOtherDesc} onChange={e => setBillOtherDesc(e.target.value)} placeholder="Other charges description" style={{ ...inpS, fontSize: 12 }} />
                                    <input type="number" min="0" value={billOther} onChange={e => setBillOther(e.target.value)} placeholder="Amount" style={{ ...inpS, textAlign: 'right' }} />
                                </div>
                            </div>

                            {/* Adjustments */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4, color: '#374151' }}>DISCOUNT %</label>
                                    <input type="number" min="0" max="100" value={billDisc} onChange={e => setBillDisc(e.target.value)} style={inpS} />
                                    <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>- Rs. {fmtBill(t.discAmt)}</div>
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4, color: '#374151' }}>ADVANCE PAID</label>
                                    <input type="number" min="0" value={billAdv} onChange={e => setBillAdv(e.target.value)} style={inpS} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4, color: '#374151' }}>PAYMENT MODE</label>
                                    <select value={billMode} onChange={e => setBillMode(e.target.value)} style={{ ...inpS, background: 'white' }}>
                                        <option>Cash</option><option>Card</option><option>Bank Transfer</option><option>Insurance</option>
                                    </select>
                                </div>
                            </div>

                            {/* Totals */}
                            <div style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <span style={{ fontSize: 13, color: '#374151' }}>Sub-Total</span>
                                    <span style={{ fontSize: 13, fontWeight: 700 }}>Rs. {fmtBill(t.subtotalBefore)}</span>
                                </div>
                                {parseFloat(billDisc) > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span style={{ fontSize: 13, color: '#dc2626' }}>Discount ({billDisc}%)</span>
                                        <span style={{ fontSize: 13, color: '#dc2626', fontWeight: 600 }}>- Rs. {fmtBill(t.discAmt)}</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <span style={{ fontSize: 13, color: '#374151' }}>Net Total</span>
                                    <span style={{ fontSize: 13, fontWeight: 700 }}>Rs. {fmtBill(t.subtotal)}</span>
                                </div>
                                {parseFloat(billAdv) > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                        <span style={{ fontSize: 13, color: '#374151' }}>Advance Paid</span>
                                        <span style={{ fontSize: 13, fontWeight: 600 }}>- Rs. {fmtBill(billAdv)}</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1.5px solid #86efac' }}>
                                    <span style={{ fontSize: 16, fontWeight: 800, color: '#15803d' }}>Balance Due</span>
                                    <span style={{ fontSize: 20, fontWeight: 800, color: '#15803d' }}>Rs. {fmtBill(t.balance)}</span>
                                </div>
                            </div>

                            {/* Actions */}
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                                <button onClick={() => setShowFinalBill(false)} style={{ padding: '10px 20px', background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Close</button>
                                <button onClick={() => saveFinalBill(false)} disabled={billSaving} style={{ padding: '10px 20px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                    {billSaving ? 'Saving...' : 'Save Bill'}
                                </button>
                                <button onClick={() => saveFinalBill(true)} disabled={billSaving} style={{ padding: '10px 20px', background: '#0f766e', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                    Save + Print Bill
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}
